import type { ZodType } from 'zod';
import { AuthEndpoints } from './endpoints/auth';
import { EventosEndpoints } from './endpoints/eventos';
import { FeedEndpoint } from './endpoints/feed';
import { CandidaturasEndpoints } from './endpoints/candidaturas';
import { NotificationsEndpoints } from './endpoints/notifications';
import { CarteiraEndpoints } from './endpoints/carteira';
import { TeamEndpoints } from './endpoints/team';
import { RelatoriosEndpoints } from './endpoints/relatorios';
import { MediaEndpoints } from './endpoints/media';
import { AvaliacoesEndpoints } from './endpoints/avaliacoes';
import { PushEndpoints } from './endpoints/push';
// Phase 18 v1.4 — KYC-01/02. KYC submission (profissional) + admin review.
import { KycEndpoints } from './endpoints/kyc';
import { AdminEndpoints } from './endpoints/admin';
// Phase 19 v1.4 — PIX-01. Empresa PIX checkout (charge + status polling).
import { PagamentosEndpoints } from './endpoints/pagamentos';
// Phase 20 v1.4 — NOTIF-01. Per-kind notification preference endpoints.
import { UsersEndpoints } from './endpoints/users';
// Phase 24 v1.6 — REQ-PIX-01. Profissional default-PIX-key CRUD.
import { ProfissionaisEndpoints } from './endpoints/profissionais';
// Phase 26 — REQ-CATALOGO-01. Empresa-side catálogo de favoritos.
import { CatalogoEndpoints } from './endpoints/catalogo';
// Phase 26 — REQ-CATALOGO-01. CONVOCAR direto invite surface.
import { ConvocacoesEndpoints } from './endpoints/convocacoes';
// Phase 33 — SUP-01. Suporte ticket system.
import { SuporteEndpoints } from './endpoints/suporte';
import { EnderecosEndpoints } from './endpoints/enderecos';
import { formatValidationIssues, isZodIssueArray } from './validation-messages';

export interface ApiClientConfig {
  baseUrl: string;
  getAccessToken: () => string | null | Promise<string | null>;
  /**
   * Called on 401. Return `true` if the session was refreshed and the original
   * request should be retried once. Return `false` (or void) to let the 401
   * propagate as an ApiError so the caller can route the user to /welcome.
   */
  onUnauthorized?: () => boolean | void | Promise<boolean | void>;
  /**
   * Injected on every request as `X-Client-Platform`. Backend gates refresh-
   * token-in-body behavior on this header (web → HttpOnly cookie, capacitor →
   * body field). Omit on test/server callers — header is then never sent.
   */
  getPlatformHeader?: () => 'web' | 'capacitor';
  /** Optional callback fired when an HTTP request initiates (useful for global loading indicators). */
  onRequestStart?: () => void;
  /** Optional callback fired when an HTTP request completes or fails (useful for global loading indicators). */
  onRequestEnd?: () => void;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type FetchOptions<TResponse> = {
  body?: unknown;
  responseSchema?: ZodType<TResponse>;
  query?: Record<string, string | number | readonly string[] | undefined>;
};

export class ApiClient {
  public readonly auth: AuthEndpoints;
  public readonly eventos: EventosEndpoints;
  public readonly feed: FeedEndpoint;
  public readonly candidaturas: CandidaturasEndpoints;
  public readonly notifications: NotificationsEndpoints;
  public readonly carteira: CarteiraEndpoints;
  public readonly team: TeamEndpoints;
  public readonly relatorios: RelatoriosEndpoints;
  /** Phase 11 (MEDIA-01..03) — avatar/logo upload surface. */
  public readonly media: MediaEndpoints;
  /** Phase 13 (RATE-01..03) — bilateral post-evento ratings + public reputation. */
  public readonly avaliacoes: AvaliacoesEndpoints;
  /** Phase 15 (PUSH-01) — FCM device token register/revoke. */
  public readonly push: PushEndpoints;
  /** Phase 18 (KYC-01) — profissional-side KYC submission (multipart). */
  public readonly kyc: KycEndpoints;
  /** Phase 18 (KYC-02) — founder admin review (list/detail/decidir). */
  public readonly admin: AdminEndpoints;
  /** Phase 19 (PIX-01) — empresa PIX charge + status polling. */
  public readonly pagamentos: PagamentosEndpoints;
  /** Phase 20 (NOTIF-01) — per-kind notification preference endpoints. */
  public readonly users: UsersEndpoints;
  /** Phase 24 (REQ-PIX-01) — profissional default-PIX-key CRUD. */
  public readonly profissionais: ProfissionaisEndpoints;
  /** Phase 26 (REQ-CATALOGO-01) — empresa catálogo de favoritos. */
  public readonly catalogo: CatalogoEndpoints;
  /** Phase 26 (REQ-CATALOGO-01) — CONVOCAR direto invite surface. */
  public readonly convocacoes: ConvocacoesEndpoints;
  /** Phase 33 (SUP-01) — user-facing suporte ticket CRUD. */
  public readonly suporte: SuporteEndpoints;
  public readonly enderecos: EnderecosEndpoints;

  /**
   * Phase 16 (SOCIAL-01/02) — exposed for OAuth helpers that need to build
   * absolute URLs (e.g. `auth.googleAuthUrl()` returns `${baseUrl}/auth/google`).
   * Identical to `config.baseUrl`; promoted to a public field so endpoint
   * classes don't need to reach into the private config bag.
   */
  public readonly baseUrl: string;

  constructor(private readonly config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.auth = new AuthEndpoints(this);
    this.eventos = new EventosEndpoints(this);
    this.feed = new FeedEndpoint(this);
    this.candidaturas = new CandidaturasEndpoints(this);
    this.notifications = new NotificationsEndpoints(this);
    this.carteira = new CarteiraEndpoints(this);
    this.team = new TeamEndpoints(this);
    this.relatorios = new RelatoriosEndpoints(this);
    this.media = new MediaEndpoints(this);
    this.avaliacoes = new AvaliacoesEndpoints(this);
    this.push = new PushEndpoints(this);
    this.kyc = new KycEndpoints(this);
    this.admin = new AdminEndpoints(this);
    this.pagamentos = new PagamentosEndpoints(this);
    this.users = new UsersEndpoints(this);
    this.profissionais = new ProfissionaisEndpoints(this);
    this.catalogo = new CatalogoEndpoints(this);
    this.convocacoes = new ConvocacoesEndpoints(this);
    this.suporte = new SuporteEndpoints(this);
    this.enderecos = new EnderecosEndpoints(this);
  }

  private async request<TResponse>(
    method: string,
    path: string,
    options: FetchOptions<TResponse> = {},
    isRetry = false,
  ): Promise<TResponse> {
    if (!isRetry) {
      this.config.onRequestStart?.();
    }
    try {
      const url = new URL(`${this.config.baseUrl}${path}`);
      if (options.query) {
        for (const [k, v] of Object.entries(options.query)) {
          if (v === undefined) continue;
          if (Array.isArray(v)) {
            url.searchParams.delete(k);
            for (const item of v) {
              url.searchParams.append(k, String(item));
            }
          } else {
            url.searchParams.set(k, String(v));
          }
        }
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        skip_zrok_interstitial: 'true',
        'ngrok-skip-browser-warning': 'true',
      };

      if (this.config.getPlatformHeader) {
        headers['X-Client-Platform'] = this.config.getPlatformHeader();
      }

      const token = await this.config.getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url, {
        method,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        credentials: 'include',
      });

      if (res.status === 401 && !isRetry && this.config.onUnauthorized && path !== '/auth/refresh') {
        const refreshed = await this.config.onUnauthorized();
        if (refreshed === true) {
          return await this.request<TResponse>(method, path, options, true);
        }
      }

      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;

      if (!res.ok) {
        // nestjs-zod 400s carry `errors: ZodIssue[]` and no domain `code`;
        // surface a readable pt-BR message instead of "Validation failed".
        if (res.status === 400 && !json?.code && isZodIssueArray(json?.errors)) {
          throw new ApiError(
            res.status,
            'validation_error',
            formatValidationIssues(json.errors),
            { issues: json.errors },
          );
        }
        throw new ApiError(
          res.status,
          json?.code ?? 'unknown_error',
          json?.message ?? res.statusText,
          json?.details ?? (json?.issues !== undefined ? { issues: json.issues } : undefined),
        );
      }

      if (options.responseSchema) {
        return options.responseSchema.parse(json);
      }
      return json as TResponse;
    } finally {
      if (!isRetry) {
        this.config.onRequestEnd?.();
      }
    }
  }

  get<T>(path: string, opts?: FetchOptions<T>) {
    return this.request<T>('GET', path, opts);
  }
  post<T>(path: string, body?: unknown, opts?: FetchOptions<T>) {
    return this.request<T>('POST', path, { ...opts, body });
  }
  put<T>(path: string, body?: unknown, opts?: FetchOptions<T>) {
    return this.request<T>('PUT', path, { ...opts, body });
  }
  patch<T>(path: string, body?: unknown, opts?: FetchOptions<T>) {
    return this.request<T>('PATCH', path, { ...opts, body });
  }
  delete<T>(path: string, opts?: FetchOptions<T>) {
    return this.request<T>('DELETE', path, opts);
  }

  /**
   * Raw GET returning the response body as a string — used for CSV/text
   * endpoints that do not return JSON. Bypasses responseSchema parsing.
   * Phase 30 (PROTO-02): CSV export.
   *
   * CR-04: mirrors the 401-retry / onUnauthorized pattern from request() so
   * an expired access token triggers a transparent refresh rather than a
   * hard error during CSV export.
   */
  async getRaw(
    path: string,
    opts?: { query?: Record<string, string | undefined> },
    isRetry = false,
  ): Promise<string> {
    if (!isRetry) {
      this.config.onRequestStart?.();
    }
    try {
      const url = new URL(`${this.config.baseUrl}${path}`);
      if (opts?.query) {
        for (const [k, v] of Object.entries(opts.query)) {
          if (v !== undefined) url.searchParams.set(k, v);
        }
      }

      const headers: Record<string, string> = {
        Accept: 'text/csv,text/plain,*/*',
        skip_zrok_interstitial: 'true',
        'ngrok-skip-browser-warning': 'true',
      };

      if (this.config.getPlatformHeader) {
        headers['X-Client-Platform'] = this.config.getPlatformHeader();
      }

      const token = await this.config.getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (res.status === 401 && !isRetry && this.config.onUnauthorized && path !== '/auth/refresh') {
        const refreshed = await this.config.onUnauthorized();
        if (refreshed === true) return await this.getRaw(path, opts, true);
      }

      if (!res.ok) {
        const text = await res.text();
        let code = 'unknown_error';
        let message = res.statusText;
        try {
          const json = JSON.parse(text);
          code = json?.code ?? code;
          message = json?.message ?? message;
        } catch {
          // non-JSON error body
        }
        throw new ApiError(res.status, code, message);
      }

      return res.text();
    } finally {
      if (!isRetry) {
        this.config.onRequestEnd?.();
      }
    }
  }

  // Phase 1 anchor — proves the client wiring works end-to-end
  health() {
    return this.get<{ status: string; db?: string; now?: string }>('/health/db');
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}

export { AuthEndpoints } from './endpoints/auth';
export { EventosEndpoints } from './endpoints/eventos';
export { FeedEndpoint } from './endpoints/feed';
export { CandidaturasEndpoints } from './endpoints/candidaturas';
export { NotificationsEndpoints } from './endpoints/notifications';
export { CarteiraEndpoints } from './endpoints/carteira';
export { TeamEndpoints } from './endpoints/team';
export { RelatoriosEndpoints } from './endpoints/relatorios';
export { MediaEndpoints } from './endpoints/media';
export { AvaliacoesEndpoints } from './endpoints/avaliacoes';
export { PushEndpoints, type PushPlatform, type RegisterPushTokenInput } from './endpoints/push';
export { KycEndpoints, type KycSubmeterInput, type KycSubmeterResult } from './endpoints/kyc';
export { AdminEndpoints, type ListKycOpts } from './endpoints/admin';
export { UsersEndpoints } from './endpoints/users';
export { CatalogoEndpoints } from './endpoints/catalogo';
export { ConvocacoesEndpoints } from './endpoints/convocacoes';
export { SuporteEndpoints } from './endpoints/suporte';
export { EnderecosEndpoints } from './endpoints/enderecos';
